import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';
import { EarthquakeZone, lookupsJson } from '@ourbranch/lookups';

import { appConfig } from 'brand-config';
import infoIcon from 'core/assets/svg/ic-info-on-card.svg';
import Field from 'core/components/form/form.v2';
import { AppliedTag } from 'common/components/farm/applied-tag';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';

import useStyles from './sub-risk.styles';

const defaultEarthquakeOptions = lookupsJson.farm.earthquakeDeductible;

// Zip-based EQ zone → single info-row callout below the fields (issue #32439). Mirrors the
// golf-cart / inland-marine alert pattern (see inland-marine.jsx `alertMessages`) — one line,
// icon + text, at the bottom of the section. Zone 1 is the default for unlisted MO zips (and
// non-CFM brands / missing zip), labeled explicitly for underwriter clarity per Amber's ticket
// comment. Zone enum + zip map centralized in @ourbranch/lookups.
const zoneMessage = (zone) => {
  if (zone === EarthquakeZone.Excluded) {
    return 'This dwelling or structure is in Zone 4 — earthquake coverage cannot be offered.';
  }
  if (zone === EarthquakeZone.MustApprove) {
    return 'This dwelling or structure is in Zone 2, which requires underwriter approval if earthquake coverage is applied.';
  }
  return 'This dwelling or structure is in Zone 1 — earthquake coverage does not require underwriter approval.';
};

const EarthquakeCoverageForm = ({ fieldName, disabled, riskType, state, zip }) => {
  const classes = useStyles();
  const { values } = useFormikContext();

  const stateConfig = appConfig.farm?.[state];
  const isEarthquakeCoverageHidden = stateConfig?.risks?.[riskType]?.hiddenCoverages?.includes('earthquakeCoverage');

  if (isEarthquakeCoverageHidden) {
    return null;
  }

  const options = stateConfig?.earthquakeDeductibleOptions || defaultEarthquakeOptions;
  const zone = zip ? stateConfig?.earthquakeZonesByZip?.[zip] : undefined;
  const isExcluded = zone === EarthquakeZone.Excluded;
  const message = zoneMessage(zone);

  // Coverage A field ("Dwelling and Contents") and Coverage B field ("Related Private Structures")
  // per issue #32439. Field↔rater semantic mapping is a follow-up for the CFM MO rating ticket
  // — see dwelling-risk-answers.handler.ts for the ClarionDoor eq_deductible /
  // eq_ded_rel_priv_struct note.
  const deductibleEarthquakeDwellingContents = getIn(values, `${fieldName}.deductibleEarthquakeDwellingContents`);
  const deductibleEarthquake = getIn(values, `${fieldName}.deductibleEarthquake`);

  const isApplied = !!deductibleEarthquakeDwellingContents || !!deductibleEarthquake;

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Earthquake coverage</div>}
      contentRight={isApplied ? <AppliedTag /> : null}
      initialOpen={false}
    >
      <Grid container spacing={2}>
        {isExcluded ? (
          <>
            {/* Zone 4: render read-only "No coverage" text instead of a disabled select so it
                doesn't look broken and is clearly not interactive (issue #32439 feedback). */}
            <Field
              xs={6}
              type="value"
              id={`${fieldName}.deductibleEarthquakeDwellingContents`}
              name={`${fieldName}.deductibleEarthquakeDwellingContents`}
              mode="dark"
              label="Earthquake - Dwelling and Contents"
              value="No coverage"
            />
            <Field
              xs={6}
              type="value"
              id={`${fieldName}.deductibleEarthquake`}
              name={`${fieldName}.deductibleEarthquake`}
              mode="dark"
              label="Earthquake - Related Private Structures"
              value="No coverage"
            />
          </>
        ) : (
          <>
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.deductibleEarthquakeDwellingContents`}
              name={`${fieldName}.deductibleEarthquakeDwellingContents`}
              mode="dark"
              label="Earthquake - Dwelling and Contents"
              options={options}
              disabled={disabled}
            />
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.deductibleEarthquake`}
              name={`${fieldName}.deductibleEarthquake`}
              mode="dark"
              label="Earthquake - Related Private Structures"
              options={options}
              disabled={disabled}
            />
            {/* The "Earthquake exterior masonry veneer exclusion" checkbox was removed (#34221):
                the FO-554 earthquake form is hardcoded with the correct response, so the field
                was a no-op (it unchecked itself on re-rate). */}
          </>
        )}
        <Grid item xs={12} className={classes.alertRow}>
          <div className={classes.alert}>
            <img src={infoIcon} className={classes.alertIconInline} alt="Zone info" />
            <div className={classes.alertMessageInline}>{message}</div>
          </div>
        </Grid>
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

EarthquakeCoverageForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  riskType: PropTypes.string.isRequired,
  state: PropTypes.string,
  zip: PropTypes.string,
  disabled: PropTypes.bool
};

EarthquakeCoverageForm.defaultProps = {
  disabled: false,
  state: undefined,
  zip: undefined
};

export default observer(EarthquakeCoverageForm);
